import { useQuery } from "@tanstack/react-query";
import {
  Check,
  Eye,
  Link2,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { GoalStatus } from "~/../drizzle/schema";
import { Button } from "~/components/ui/button";
import { ConfirmationDialog } from "~/components/ui/confirmation-dialog";
import {
  Drawer,
  DrawerDescription,
  DrawerTitle,
  SideDrawerBody,
  SideDrawerContent,
  SideDrawerFooter,
  SideDrawerHeader,
} from "~/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  GOAL_TIMELINE_OPTIONS,
  GoalStatusBadge,
} from "~/components/ui/goal-badges";
import { RichTextViewer } from "~/components/ui/rich-text-editor";
import { useDecryptedResource } from "~/lib/crypto";
import type { GetKeyGrantFn, KeyManager } from "~/lib/crypto";
import {
  goalProgramsQueryOptions,
  programGoalsUnderQueryOptions,
  useUnlinkGoalFromProgram,
} from "~/lib/queries/goalLinkQuery";
import { useSetGoalOfLifeDone } from "~/lib/queries/goalOfLifeQuery";
import {
  milestonesByGoalQueryOptions,
  useUpdateMilestoneStatus,
} from "~/lib/queries/milestoneQuery";
import type { DecryptedMilestone } from "~/lib/queries/milestoneQuery";

import type { GoalItem } from "./goal-groups";
import { useCategoryName } from "./goal-list";
import {
  GoalProgramLinks,
  ProgramGoalsUnder,
  removeLinkConsequences,
  type GoalLink,
} from "./goal-program-link";
import { GoalSteps, StepProgress } from "./goal-steps";

/** The goal's text, decrypted, for the forms and dialogs the panel opens. */
export type DecryptedGoal = { title: string; description: string | null };

type OwnerActions = {
  /** Whether the client shares their goals with all their coaches. */
  isSharedWithCoaches: boolean;
  onEdit: (goal: DecryptedGoal) => void;
  onShare: (goal: DecryptedGoal & { categoryName: string }) => void;
  onDelete: (goal: DecryptedGoal & { stepCount: number }) => void;
};

function stepsLabel(count: number): string {
  return `${count} ${count === 1 ? "step" : "steps"}`;
}

/**
 * Who can read the goal: the owner, her coaches when goals are shared, the
 * coach of each program it is in, anyone with its public link.
 */
function whoCanSee({
  isSharedWithCoaches,
  hasPublicLink,
  programCoaches,
}: {
  isSharedWithCoaches: boolean;
  hasPublicLink: boolean;
  programCoaches: readonly string[];
}): string {
  const readers = isSharedWithCoaches
    ? "You and all your coaches."
    : programCoaches.length > 0
      ? `You, and ${programCoaches.join(" and ")} through your ${programCoaches.length === 1 ? "program" : "programs"}.`
      : "Only you.";
  return hasPublicLink
    ? `${readers} Anyone with its public link can see it too.`
    : readers;
}

function GoalMenu({
  hasPublicLink,
  disabled,
  programLinks,
  onEdit,
  onShare,
  onRemoveFromProgram,
  onDelete,
}: {
  hasPublicLink: boolean;
  disabled: boolean;
  /** The programs the goal is in, one "Remove" item each. */
  programLinks: readonly GoalLink[];
  onEdit: () => void;
  onShare: () => void;
  onRemoveFromProgram: (link: GoalLink) => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Goal actions"
          disabled={disabled}
        >
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil aria-hidden />
          Edit details
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onShare}>
          <Link2 aria-hidden />
          {hasPublicLink ? "Manage public link" : "Share a public link"}
        </DropdownMenuItem>
        {programLinks.map((link) => (
          <DropdownMenuItem
            key={link.engagementId}
            onSelect={() => onRemoveFromProgram(link)}
          >
            <X aria-hidden />
            Remove from {link.coachFirstName}&apos;s program
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem variant="destructive" onSelect={onDelete}>
          <Trash2 aria-hidden />
          Delete goal
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * One goal, read and worked on in one place. The client ticks, adds and
 * reorders steps, marks the goal done and reopens it; every change saves as
 * she makes it. Her coach reads the same panel without any controls.
 */
export function GoalPanel({
  item,
  open,
  onOpenChange,
  keyManager,
  getKeyGrant,
  owner,
  hideCategoryName = false,
}: {
  item: GoalItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyManager: KeyManager | null;
  getKeyGrant?: GetKeyGrantFn;
  /** The client's own controls; without them the panel is a coach's read-only view. */
  owner?: OwnerActions;
  hideCategoryName?: boolean;
}) {
  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <SideDrawerContent>
        {item && (
          <GoalPanelContent
            // A different goal starts with its own panel state.
            key={item.goal.id}
            item={item}
            keyManager={keyManager}
            getKeyGrant={getKeyGrant}
            owner={owner}
            hideCategoryName={hideCategoryName}
          />
        )}
      </SideDrawerContent>
    </Drawer>
  );
}

function GoalPanelContent({
  item,
  keyManager,
  getKeyGrant,
  owner,
  hideCategoryName,
}: {
  item: GoalItem;
  keyManager: KeyManager | null;
  getKeyGrant?: GetKeyGrantFn;
  owner?: OwnerActions;
  hideCategoryName: boolean;
}) {
  const { goal, category } = item;
  const isDone = goal.status === GoalStatus.COMPLETED;

  const { data: decrypted, isDecrypting } = useDecryptedResource({
    resourceType: "goal",
    resourceId: goal.id,
    keyManager,
    getKeyGrant,
    fields: {
      title: { encrypted: goal.encryptedTitle },
      description: { encrypted: goal.encryptedDescription },
    },
  });
  const { name: categoryName } = useCategoryName({
    category,
    keyManager,
    getKeyGrant,
    hideName: hideCategoryName,
  });
  const {
    data: steps = [],
    isLoading: stepsLoading,
    isError: stepsFailed,
    refetch: refetchSteps,
  } = useQuery(
    milestonesByGoalQueryOptions({
      goalId: goal.id,
      viewer: owner ? "owner" : "coach",
      keyManager,
    }),
  );

  const { data: programs } = useQuery({
    ...goalProgramsQueryOptions(goal.id),
    enabled: !!owner,
  });
  const programLinks = programs?.links ?? [];
  const { data: programGoals = [] } = useQuery({
    ...programGoalsUnderQueryOptions(goal.id),
    enabled: !!owner,
  });
  const unlink = useUnlinkGoalFromProgram();
  const [removing, setRemoving] = useState<GoalLink | null>(null);

  const removeFromProgram = (link: GoalLink) =>
    unlink.mutate(
      { goalId: goal.id, engagementId: link.engagementId },
      {
        onSuccess: () => {
          setRemoving(null);
          toast.success(
            link.status === "active"
              ? `Removed from ${link.coachFirstName}'s program`
              : "Suggestion cancelled",
          );
        },
        onError: () => toast.error("The goal was not removed. Try again."),
      },
    );

  const updateStatus = useUpdateMilestoneStatus();
  const setDone = useSetGoalOfLifeDone();
  // Ticks show at once; the saved state replaces them when it comes back.
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [declinedFinish, setDeclinedFinish] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [doneFailed, setDoneFailed] = useState<boolean | null>(null);

  const ticked = (step: DecryptedMilestone) =>
    ticks[step.id] ?? step.status === GoalStatus.COMPLETED;
  const doneCount = steps.filter(ticked).length;
  const openCount = steps.length - doneCount;

  const tick = (step: DecryptedMilestone, checked: boolean) => {
    setTicks((current) => ({ ...current, [step.id]: checked }));
    if (checked) setDeclinedFinish(false);
    updateStatus.mutate(
      {
        id: step.id,
        status: checked ? GoalStatus.COMPLETED : GoalStatus.PLANNED,
      },
      {
        onError: () => toast.error("The step was not saved. Try again."),
        // A later click on the same step keeps its own tick showing.
        onSettled: () =>
          setTicks((current) => {
            if (current[step.id] !== checked) return current;
            const { [step.id]: _settled, ...rest } = current;
            return rest;
          }),
      },
    );
  };

  const saveDone = (done: boolean) => {
    setDoneFailed(null);
    setConfirmFinish(false);
    setDone.mutate(
      { id: goal.id, done },
      { onError: () => setDoneFailed(done) },
    );
  };

  const markDone = () => {
    if (openCount > 0) setConfirmFinish(true);
    else saveDone(true);
  };

  const title = decrypted.title ?? "Untitled";
  const description = decrypted.description;
  const timeline = GOAL_TIMELINE_OPTIONS.find(
    (option) => option.id === goal.timeline,
  )?.label;
  const offerFinish =
    !!owner &&
    !isDone &&
    !declinedFinish &&
    steps.length > 0 &&
    openCount === 0;
  const decryptedGoal = { title: decrypted.title ?? "", description };

  return (
    <>
      <SideDrawerHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <p className="eyebrow text-gray-500">
              {[categoryName, timeline].filter(Boolean).join(" · ")}
            </p>
            <DrawerTitle className="font-heading text-xl font-medium break-words text-gray-900">
              {isDecrypting ? (
                <span className="inline-block h-7 w-56 animate-pulse rounded bg-gray-100" />
              ) : (
                title
              )}
            </DrawerTitle>
          </div>
          {owner && (
            <GoalMenu
              hasPublicLink={!!goal.shareSlug}
              // The menu's forms and the public link use the decrypted text.
              disabled={isDecrypting}
              programLinks={programLinks.filter(
                (link) => link.status === "active",
              )}
              onRemoveFromProgram={setRemoving}
              onEdit={() => owner.onEdit(decryptedGoal)}
              onShare={() =>
                owner.onShare({
                  ...decryptedGoal,
                  categoryName: categoryName ?? "",
                })
              }
              onDelete={() =>
                owner.onDelete({ ...decryptedGoal, stepCount: steps.length })
              }
            />
          )}
        </div>
      </SideDrawerHeader>

      <SideDrawerBody className="space-y-6 py-6">
        <div className="space-y-4">
          <GoalStatusBadge status={goal.status} />
          <DrawerDescription asChild>
            <div className="text-text-secondary text-sm">
              {isDecrypting ? (
                <span className="block h-4 w-3/4 animate-pulse rounded bg-gray-100" />
              ) : (
                description && <RichTextViewer content={description} />
              )}
            </div>
          </DrawerDescription>
        </div>

        {owner && programs && (
          <GoalProgramLinks
            goalId={goal.id}
            programs={programs}
            keyManager={keyManager}
            onCancelSuggestion={removeFromProgram}
          />
        )}

        {offerFinish && (
          <div
            role="status"
            className="space-y-3 rounded-2xl border border-green-200 bg-green-50 p-4"
          >
            <p className="text-sm text-green-800">
              All steps are done. Mark this goal as done?
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => saveDone(true)}
                disabled={setDone.isPending}
              >
                <Check aria-hidden />
                Mark as done
              </Button>
              <Button size="sm" onClick={() => setDeclinedFinish(true)}>
                Not yet
              </Button>
            </div>
          </div>
        )}

        <section className="space-y-3" aria-labelledby="goal-steps-heading">
          <div className="flex items-baseline justify-between gap-4">
            <h3
              id="goal-steps-heading"
              className="text-base font-medium text-gray-900"
            >
              Steps
            </h3>
            {steps.length > 0 && (
              <span className="text-text-tertiary text-sm tabular-nums">
                {doneCount} of {steps.length} done
              </span>
            )}
          </div>
          <StepProgress total={steps.length} done={doneCount} />
          {stepsFailed ? (
            <p className="flex flex-wrap items-center gap-2 text-sm text-red-700">
              The steps did not load.
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0 text-red-700"
                onClick={() => void refetchSteps()}
              >
                Try again
              </Button>
            </p>
          ) : stepsLoading ? (
            <div className="space-y-2" aria-hidden>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-9 animate-pulse rounded bg-gray-100"
                />
              ))}
            </div>
          ) : (
            <GoalSteps
              goal={goal}
              steps={steps}
              readOnly={!owner}
              keyManager={keyManager}
              ticked={ticked}
              onTick={tick}
            />
          )}
        </section>

        <ProgramGoalsUnder goals={programGoals} keyManager={keyManager} />

        {owner && (
          <div className="space-y-1 rounded-xl bg-gray-50 p-4 text-sm">
            <p className="flex items-center gap-2 font-medium text-gray-900">
              <Eye className="size-4" aria-hidden />
              Who can see this goal
            </p>
            <p className="text-text-secondary">
              {whoCanSee({
                isSharedWithCoaches: owner.isSharedWithCoaches,
                hasPublicLink: !!goal.shareSlug,
                programCoaches: programLinks.map((link) => link.coachFirstName),
              })}
            </p>
          </div>
        )}
      </SideDrawerBody>

      {owner && (
        <SideDrawerFooter className="justify-between">
          <span className="text-text-tertiary text-sm">
            {doneFailed !== null ? (
              <span className="flex items-center gap-2 text-red-700">
                Not saved
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto px-0 text-red-700"
                  onClick={() => saveDone(doneFailed)}
                >
                  Retry
                </Button>
              </span>
            ) : (
              "Changes save as you go"
            )}
          </span>
          {isDone ? (
            <Button
              onClick={() => saveDone(false)}
              disabled={setDone.isPending}
            >
              Reopen
            </Button>
          ) : (
            <Button
              onClick={markDone}
              // The open steps decide whether finishing asks first.
              disabled={setDone.isPending || stepsLoading || stepsFailed}
            >
              <Check aria-hidden />
              Mark goal as done
            </Button>
          )}
        </SideDrawerFooter>
      )}

      <ConfirmationDialog
        open={!!removing}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={
          removing
            ? `Remove this goal from ${removing.coachFirstName}'s program?`
            : "Remove this goal from the program?"
        }
        description={removing ? removeLinkConsequences(removing) : ""}
        confirmText="Remove from program"
        onConfirm={() => removing && removeFromProgram(removing)}
        isLoading={unlink.isPending}
      />

      <ConfirmationDialog
        open={confirmFinish}
        onOpenChange={setConfirmFinish}
        title="Mark this goal as done?"
        description={`${stepsLabel(openCount)} ${openCount === 1 ? "is" : "are"} still open. You can reopen the goal later.`}
        confirmText="Mark as done"
        cancelText="Not yet"
        onConfirm={() => saveDone(true)}
        isLoading={setDone.isPending}
      />
    </>
  );
}
